import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import {
  BarChart3,
  Bell,
  Download,
  Loader2,
  LogIn,
  Mail,
  MousePointerClick,
  Sparkles,
  Gift,
  Trophy,
  Wallet,
} from "lucide-react";
import { getMyDashboard, saveMyAutoBid } from "@/lib/dashboard.functions";
import { BID_STEP, badgesFor, formatIDR, levelFor } from "@/lib/board-constants";
import { useSession } from "@/lib/auth";
import {
  getMyAnalytics,
  getMyEmailPref,
  getMyGrowthNotifications,
  getMyReferral,
  setMyEmailPref,
} from "@/lib/growth.functions";
import { track, withUtm } from "@/lib/analytics";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { CountUp, Reveal, ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/akun")({
  head: () => ({
    meta: [
      { title: "Dashboard Saya — jasasebar.com" },
      {
        name: "description",
        content:
          "Dashboard pribadi: pantau peringkat listing, transaksi, status auto-bid, poin, dan badge dari akun Google Anda.",
      },
      { property: "og:title", content: "Dashboard Saya — jasasebar.com" },
      { property: "og:description", content: "Peringkat, transaksi, auto-bid, poin, dan badge akun Anda." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AkunPage,
});

function AkunPage() {
  const session = useSession();
  const dashFn = useServerFn(getMyDashboard);
  const autoBidFn = useServerFn(saveMyAutoBid);
  const qc = useQueryClient();

  const key = ["my-dashboard", session.email ?? "-"];
  const dash = useQuery({
    queryKey: key,
    queryFn: async () => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return dashFn({ data: { idToken, ordersPerListing: 5 } });
    },
    enabled: session.isAuthenticated,
    staleTime: 20_000,
  });

  const entries = dash.data ?? [];
  const totalSpent = entries.reduce((s, e) => s + e.listing.total_amount, 0);
  const totalClicks = entries.reduce((s, e) => s + e.listing.clicks, 0);
  const totalPoints = entries.reduce((s, e) => s + e.listing.points, 0);

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Dashboard saya</h1>
            <p className="text-sm text-muted-foreground">
              {session.isAuthenticated
                ? `Listing milik ${session.email} — peringkat, transaksi, auto-bid, dan poin.`
                : "Masuk dengan Google untuk melihat listing milik Anda."}
            </p>
          </div>
          {session.isAuthenticated && (
            <Button asChild size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
              <Link to="/rekap">
                <BarChart3 className="size-3.5" /> Rekap performa
              </Link>
            </Button>
          )}
        </div>

        {session.isLoading ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : !session.isAuthenticated ? (
          <Card className="mt-6">
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-muted-foreground">
                Dashboard bersifat pribadi. Masuk dengan Google untuk melihat listing Anda.
              </p>
              <Button onClick={session.login} className="gap-1.5">
                <LogIn className="size-4" /> Masuk dengan Google
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <ReferralCard session={session} />
            <GrowthNotificationsCard session={session} />
            <EmailPrefCard session={session} />
            <TrafficCard session={session} />

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <StatCard icon={Wallet} label="Total nilai bid" value={totalSpent} money />
              <StatCard icon={MousePointerClick} label="Total klik" value={totalClicks} />
              <StatCard icon={Sparkles} label="Total poin" value={totalPoints} />
            </div>

            {dash.isPending && (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Memuat listing…
              </div>
            )}
            {dash.isError && (
              <p className="mt-6 text-sm text-destructive">{(dash.error as Error).message}</p>
            )}

            {!dash.isPending && entries.length === 0 && (
              <Card className="mt-4">
                <CardContent className="p-6 text-center text-sm text-muted-foreground">
                  Belum ada listing pada akun ini.{" "}
                  <Link to="/" hash="daftar" className="text-primary hover:underline">
                    Daftarkan jasa Anda gratis
                  </Link>
                  .
                </CardContent>
              </Card>
            )}

            <div className="mt-4 space-y-3">
              {entries.map((entry, i) => (
                <ListingPanel
                  key={entry.listing.slug}
                  entry={entry}
                  index={i}
                  onSave={async (max) => {
                    const idToken = await session.getIdToken();
                    if (!idToken) throw new Error("Sesi tidak ditemukan.");
                    await autoBidFn({ data: { idToken, slug: entry.listing.slug, max } });
                    await qc.invalidateQueries({ queryKey: key });
                  }}
                />
              ))}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function ReferralCard({ session }: { session: ReturnType<typeof useSession> }) {
  const refFn = useServerFn(getMyReferral);
  const [copied, setCopied] = useState(false);
  const q = useQuery({
    queryKey: ["my-referral", session.email ?? "-"],
    queryFn: async () => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return refFn({ data: { idToken } });
    },
    enabled: session.isAuthenticated,
  });

  const link =
    typeof window !== "undefined" && q.data
      ? withUtm(`${window.location.origin}/`, {
          source: "referral",
          medium: "invite",
          campaign: "referral_program",
          content: q.data.code,
        }) + `&ref=${q.data.code}`
      : "";

  return (
    <Card className="mt-4">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <Gift className="size-4 text-primary" />
          <h2 className="font-display text-sm font-semibold">Program referral</h2>
        </div>
        {q.isPending && <Skeleton className="h-16 w-full" />}
        {q.data && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="font-mono text-sm">{q.data.code}</Badge>
              <span className="text-xs text-muted-foreground">
                {q.data.invited} undangan · {q.data.converted} sukses
              </span>
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={link}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(link);
                  track("referral_copy", { code: q.data.code });
                  setCopied(true);
                  toast.success("Link referral disalin.");
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? "Tersalin" : "Salin"}
              </Button>
            </div>
            {q.data.recent.length > 0 && (
              <ul className="space-y-1 text-xs text-muted-foreground">
                {q.data.recent.map((r) => (
                  <li key={r.email} className="flex justify-between">
                    <span>{r.email}</span>
                    <span>{r.converted ? "Sukses" : "Menunggu listing"}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              Undangan dihitung sukses saat orang yang Anda ajak membuat listing pertamanya.
            </p>
            <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
              <Link to="/referral">Lihat papan peringkat referral</Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function GrowthNotificationsCard({ session }: { session: ReturnType<typeof useSession> }) {
  const notifFn = useServerFn(getMyGrowthNotifications);
  const q = useQuery({
    queryKey: ["growth-notifications", session.email ?? "-"],
    queryFn: async () => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return notifFn({ data: { idToken } });
    },
    enabled: session.isAuthenticated,
    refetchInterval: 60_000,
  });

  if (!q.data || q.data.length === 0) return null;

  return (
    <Card className="mt-3">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center gap-2">
          <Bell className="size-4 text-primary" />
          <h2 className="font-display text-sm font-semibold">Notifikasi referral</h2>
        </div>
        <ul className="space-y-2">
          {q.data.map((n) => (
            <li key={n.id} className="rounded-lg border border-border/60 p-2">
              <p className="text-xs font-medium">{n.title}</p>
              <p className="text-xs text-muted-foreground">{n.body}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function EmailPrefCard({ session }: { session: ReturnType<typeof useSession> }) {
  const getPref = useServerFn(getMyEmailPref);
  const setPref = useServerFn(setMyEmailPref);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["email-pref", session.email ?? "-"],
    queryFn: async () => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return getPref({ data: { idToken } });
    },
    enabled: session.isAuthenticated,
  });

  const m = useMutation({
    mutationFn: async (enabled: boolean) => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return setPref({ data: { idToken, enabled } });
    },
    onSuccess: (res) => {
      qc.setQueryData(["email-pref", session.email ?? "-"], { enabled: res.enabled });
      toast.success(res.enabled ? "Notifikasi email diaktifkan." : "Notifikasi email dinonaktifkan.");
    },
    onError: () => toast.error("Gagal menyimpan pengaturan. Coba lagi."),
  });

  if (!session.isAuthenticated) return null;

  return (
    <Card className="mt-3">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <Mail className="size-4 text-primary" />
          <h2 className="font-display text-sm font-semibold">Pengaturan notifikasi</h2>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Label htmlFor="email-notif" className="text-xs font-medium">
              Email notifikasi referral
            </Label>
            <p className="text-xs text-muted-foreground">
              Kirim email ke {session.email ?? "akun Anda"} saat undangan referral berhasil membuat listing
              pertamanya.
            </p>
          </div>
          <Switch
            id="email-notif"
            checked={q.data?.enabled ?? true}
            disabled={q.isLoading || m.isPending}
            onCheckedChange={(v: boolean) => m.mutate(v)}
          />
        </div>
      </CardContent>
    </Card>
  );
}


function TrafficCard({ session }: { session: ReturnType<typeof useSession> }) {
  const analyticsFn = useServerFn(getMyAnalytics);
  const [days, setDays] = useState("30");
  const [source, setSource] = useState("all");
  const [campaign, setCampaign] = useState("all");

  const q = useQuery({
    queryKey: ["my-analytics", session.email ?? "-", days, source, campaign],
    queryFn: async () => {
      const idToken = await session.getIdToken();
      if (!idToken) throw new Error("Sesi tidak ditemukan.");
      return analyticsFn({
        data: {
          idToken,
          days: Number(days),
          ...(source !== "all" ? { source } : {}),
          ...(campaign !== "all" ? { campaign } : {}),
        },
      });
    },
    enabled: session.isAuthenticated,
  });

  const exportCsv = () => {
    const rows = q.data?.rows ?? [];
    if (rows.length === 0) {
      toast.error("Belum ada data untuk diekspor.");
      return;
    }
    const header = "tanggal,slug,event,sumber,medium,campaign,total";
    const body = rows
      .map((r) => [r.day, r.slug, r.event, r.source, r.medium, r.campaign, r.total].join(","))
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analitik-jasasebar-${days}hari.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV diunduh.");
  };

  return (
    <Card className="mt-3">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="size-4 text-primary" />
            <h2 className="font-display text-sm font-semibold">Analitik traffic</h2>
          </div>
          <Button size="sm" variant="outline" onClick={exportCsv}>
            <Download className="size-3.5" /> CSV
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 hari</SelectItem>
              <SelectItem value="30">30 hari</SelectItem>
              <SelectItem value="90">90 hari</SelectItem>
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sumber" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua sumber</SelectItem>
              {q.data?.filters.sources.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={campaign} onValueChange={setCampaign}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Campaign" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua campaign</SelectItem>
              {q.data?.filters.campaigns.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {q.isPending && <Skeleton className="h-24 w-full" />}

        {q.data && q.data.bySource.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Belum ada aksi bagikan atau pemasangan widget yang tercatat.
          </p>
        )}

        {q.data && q.data.bySource.length > 0 && (
          <ul className="space-y-1 text-xs">
            {q.data.bySource.map((row) => (
              <li key={`${row.source}-${row.event}`} className="flex justify-between">
                <span className="text-muted-foreground">
                  {row.source} · {row.event}
                </span>
                <span className="font-medium tabular-nums">{row.total}</span>
              </li>
            ))}
          </ul>
        )}

        {q.data && q.data.daily.length > 0 && (
          <>
            <Separator />
            <p className="text-xs font-medium">Breakdown campaign per hari</p>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs no-scrollbar">
              {q.data.daily.map((d) => (
                <li key={`${d.day}-${d.campaign}`} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {d.day} · {d.campaign}
                  </span>
                  <span className="tabular-nums">{d.total}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {q.data && q.data.variants.length > 0 && (
          <>
            <Separator />
            <p className="text-xs font-medium">A/B kartu OG</p>
            <ul className="space-y-1 text-xs">
              {q.data.variants.map((v) => (
                <li key={v.variant} className="flex justify-between">
                  <span className="text-muted-foreground">Varian {v.variant.toUpperCase()}</span>
                  <span className="tabular-nums">
                    {v.views} tayang · {v.clicks} aksi · CTR {v.ctr}%
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  money,
}: {
  icon: typeof Wallet;
  label: string;
  value: number;
  money?: boolean;
}) {
  return (
    <Reveal>
      <Card className="p-3">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon className="size-3.5 text-primary" /> {label}
        </span>
        <div className="mt-1 font-display text-xl font-semibold tabular-nums">
          <CountUp value={value} {...(money ? { format: (v: number) => formatIDR(Math.round(v)) } : {})} />
        </div>
      </Card>
    </Reveal>
  );
}

type Entry = Awaited<ReturnType<typeof getMyDashboard>>[number];

function ListingPanel({
  entry,
  index,
  onSave,
}: {
  entry: Entry;
  index: number;
  onSave: (max: number) => Promise<void>;
}) {
  const { listing, orders, rank, orderTotal } = entry;
  const [max, setMax] = useState(listing.auto_bid_max);
  const level = levelFor(listing.points);
  const badges = badgesFor(listing);

  const save = useMutation({
    mutationFn: () => onSave(max),
    onSuccess: () => toast.success("Batas auto-bid diperbarui."),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 className="font-display text-base font-semibold">{listing.name}</h2>
                <Badge
                  variant={listing.status === "approved" ? "default" : "secondary"}
                  className="px-1.5 py-0 text-[10px]"
                >
                  {listing.status}
                </Badge>
              </div>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{listing.description}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  {listing.category}
                </Badge>
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  Lv{level.level} · {level.label} · {listing.points} poin
                </Badge>
                {badges.map((b) => (
                  <Badge key={b} variant="secondary" className="px-1.5 py-0 text-[10px]">
                    {b}
                  </Badge>
                ))}
              </div>
              {listing.admin_note && (
                <p className="mt-2 rounded-md bg-secondary p-2 text-[11px] text-muted-foreground">
                  Catatan admin: {listing.admin_note}
                </p>
              )}
            </div>

            <div className="text-right">
              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                <Trophy className="size-3.5 text-primary" /> {rank ? `Peringkat #${rank}` : "Tidak tampil"}
              </div>
              <div className="font-display text-lg font-semibold tabular-nums">
                {listing.total_amount > 0 ? formatIDR(listing.total_amount) : "GRATIS"}
              </div>
              <div className="mt-1.5 flex justify-end gap-1.5">
                <Button asChild size="sm" variant="outline" className="h-7 px-2 text-[11px]">
                  <Link to="/produk/$slug" params={{ slug: listing.slug }}>
                    Detail
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          <div className="grid gap-3 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-lg border border-border p-3">
              <h3 className="text-xs font-medium">Status auto-bid</h3>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {listing.auto_bid_max > 0
                  ? `Aktif sampai ${formatIDR(listing.auto_bid_max)}.`
                  : "Nonaktif — isi anggaran maksimum."}
              </p>
              <div className="mt-2 flex gap-1.5">
                <div className="flex h-8 flex-1 items-center gap-1 rounded-md border border-input bg-background px-2">
                  <span className="text-xs text-muted-foreground">Rp</span>
                  <input
                    type="number"
                    min={0}
                    step={BID_STEP}
                    value={max}
                    onChange={(e) => setMax(Number(e.target.value))}
                    className="w-full bg-transparent text-xs outline-none"
                  />
                </div>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {save.isPending && <Loader2 className="size-3.5 animate-spin" />} Simpan
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-medium">Transaksi terakhir</h3>
                <span className="text-[10px] text-muted-foreground">{orderTotal} transaksi</span>
              </div>
              {orders.length === 0 ? (
                <p className="mt-1.5 text-[11px] text-muted-foreground">Belum ada transaksi.</p>
              ) : (
                <ul className="mt-1.5 divide-y divide-border/70">
                  {orders.map((o) => (
                    <li key={o.merchant_order_id} className="flex items-center justify-between gap-2 py-1.5 text-[11px]">
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {o.kind === "bid" ? "Naik peringkat" : `Paket ${o.package_id ?? "-"}`} ·{" "}
                          {formatIDR(o.amount)}
                        </p>
                        <p className="truncate text-muted-foreground">
                          {new Date(o.created_at).toLocaleString("id-ID")}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Badge variant={o.status === "PAID" ? "default" : "secondary"} className="px-1.5 py-0 text-[10px]">
                          {o.status}
                        </Badge>
                        <Button asChild size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]">
                          <Link to="/transaksi/$orderId" params={{ orderId: o.merchant_order_id }}>
                            Detail
                          </Link>
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
